import React, { useEffect, useState } from "react";
import { MenuItem, Stack } from "@mui/material";
import { useTranslation } from "react-i18next";
import color from "../../../Constants/colors";
import { useWorkspaceColors } from "../../../Contexts/WorkspaceColorContext";
import { Features } from "../../../Enums/companyStatus";
import { hasPermission } from "../../../Helpers/authHelpers";
import { VoicesConfigPayload } from "../../../Models/voicesConfig";
import {
  useUpdateVoicesConfig,
  useVoicesConfig,
} from "../../../Query/useVoicesConfig";
import Loading from "../../Common/Loading";
import { ComoButton } from "../../Common/ComoButton";
import VoicesCard from "../Common/VoicesCard";
import VoicesField from "../Common/VoicesField";
import VoicesInput from "../Common/VoicesInput";
import VoicesBufferConnection from "./VoicesBufferConnection";
import VoicesCompanyVoice from "./VoicesCompanyVoice";
import VoicesSchedules from "./VoicesSchedules";

const MODEL_FIELDS: (keyof VoicesConfigPayload)[] = [
  "scoring_model_id",
  "draft_model_id",
  "claude_search_model_id",
  "search_agent_model_id",
];

const MAX_TOPIC_SCORE = 18;

const VoicesSettings: React.FC = () => {
  const { t } = useTranslation("voicesSettings");
  const { colors: workspace } = useWorkspaceColors();
  const { data: config, isLoading } = useVoicesConfig(true);
  const updateConfig = useUpdateVoicesConfig();
  const canManage = hasPermission(Features.VOICES_MARKETING);
  const [form, setForm] = useState<VoicesConfigPayload | null>(null);

  useEffect(() => {
    if (!config) return;
    setForm({
      holiday_state: config.holiday_state,
      scoring_model_id: config.scoring_model_id,
      draft_model_id: config.draft_model_id,
      search_agent_model_id: config.search_agent_model_id,
      claude_search_model_id: config.claude_search_model_id,
      engage_days: config.engage_days,
      min_topic_score: config.min_topic_score,
    });
  }, [config]);

  if (isLoading || !config || !form) {
    return (
      <Loading
        backgroundColor={color.gray50}
        color1={workspace.accent}
        color2={color.gray900}
      />
    );
  }

  const modelsFor = (field: keyof VoicesConfigPayload) =>
    config.options.models.filter(
      (model) => field !== "claude_search_model_id" || model.claude,
    );
  const isAvailable = (field: keyof VoicesConfigPayload) =>
    modelsFor(field).some((model) => model.id === form[field]);
  const modelsValid = MODEL_FIELDS.every(isAvailable);
  const engageDaysValid = form.engage_days >= 1 && form.engage_days <= 90;
  const minScoreValid =
    Number.isInteger(form.min_topic_score) &&
    form.min_topic_score >= 0 &&
    form.min_topic_score <= MAX_TOPIC_SCORE;

  return (
    <>
      <VoicesBufferConnection config={config} canManage={canManage} />
      <VoicesCompanyVoice config={config} canManage={canManage} />

      <VoicesCard
        title={t("title")}
        subtitle={t("subtitle")}
        action={
          canManage && (
            <ComoButton
              onClick={() => updateConfig.mutate(form)}
              disabled={
                !modelsValid ||
                !engageDaysValid ||
                !minScoreValid ||
                updateConfig.isPending
              }
            >
              {t("save")}
            </ComoButton>
          )
        }
      >
        <Stack spacing={3}>
          <VoicesField label={t("holidayState")} hint={t("holidayStateHint")}>
            <VoicesInput
              select
              disabled={!canManage}
              value={form.holiday_state}
              onChange={(e) => setForm({ ...form, holiday_state: e.target.value })}
            >
              {config.options.holiday_states.map((state) => (
                <MenuItem key={state.code} value={state.code}>
                  {state.name}
                </MenuItem>
              ))}
            </VoicesInput>
          </VoicesField>

          {MODEL_FIELDS.map((field) => (
            <VoicesField key={field} label={t(field)} hint={t(`${field}Hint`)}>
              <VoicesInput
                select
                disabled={!canManage}
                value={isAvailable(field) ? form[field] : ""}
                onChange={(e) =>
                  setForm({ ...form, [field]: Number(e.target.value) })
                }
                error={!isAvailable(field)}
                helperText={
                  isAvailable(field)
                    ? undefined
                    : t(
                        field === "claude_search_model_id"
                          ? "searchModelNotClaude"
                          : "modelNotAvailable",
                      )
                }
              >
                {modelsFor(field).map((model) => (
                  <MenuItem key={model.id} value={model.id}>
                    {model.name}
                  </MenuItem>
                ))}
              </VoicesInput>
            </VoicesField>
          ))}

          <VoicesField label={t("minTopicScore")} hint={t("minTopicScoreHint")}>
            <VoicesInput
              type="number"
              disabled={!canManage}
              inputProps={{ min: 0, max: MAX_TOPIC_SCORE }}
              value={form.min_topic_score}
              error={!minScoreValid}
              onChange={(e) =>
                setForm({ ...form, min_topic_score: Number(e.target.value) })
              }
            />
          </VoicesField>

          <VoicesField label={t("engageDays")} hint={t("engageDaysHint")}>
            <VoicesInput
              type="number"
              disabled={!canManage}
              inputProps={{ min: 1, max: 90 }}
              value={form.engage_days}
              error={!engageDaysValid}
              onChange={(e) =>
                setForm({ ...form, engage_days: Number(e.target.value) })
              }
            />
          </VoicesField>
        </Stack>
      </VoicesCard>
      <VoicesSchedules />
    </>
  );
};

export default VoicesSettings;
